/*
 * Which row's picture the pointer is currently over.
 *
 * MOUSE ONLY, DELIBERATELY. A touch tap fires pointerenter and frequently never
 * fires the matching pointerleave, so on a tablet the overlay would appear and
 * then stay there over the whole list with no way to dismiss it. `pointerType`
 * is checked rather than trusting the leave event to arrive.
 *
 * IT ALSO CLOSES ON SCROLL, and that is not belt-and-braces. Measured in a
 * browser: after scrolling 400px, the hovered thumbnail had moved 295px above the
 * pointer and NO pointerout had been dispatched - so the overlay, which dims the
 * whole window, stayed up over a row the cursor was no longer anywhere near,
 * until the mouse next moved. A scroll means the pointer is no longer on what it
 * was on, whether or not the browser says so.
 *
 * The hook holds a KEY, not a URL. The caller looks the URL up at render time,
 * so an object URL that has been revoked - the image was replaced, or the row
 * scrolled out and its bytes were released - cannot still be on screen: the
 * lookup simply misses and the overlay is not rendered.
 */

import { useCallback, useEffect, useState } from 'react';

export interface HoverPreview {
  /** The key currently hovered, or null. */
  activeKey: string | null;
  /** Pointer props for the element that should show a preview while hovered. */
  handlers: (key: string) => {
    onPointerEnter: (event: React.PointerEvent) => void;
    onPointerMove: (event: React.PointerEvent) => void;
    onPointerLeave: () => void;
    onPointerCancel: () => void;
  };
}

export function useHoverPreview(): HoverPreview {
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const handlers = useCallback(
    (key: string) => ({
      onPointerEnter: (event: React.PointerEvent): void => {
        if (event.pointerType !== 'mouse') return;
        setActiveKey(key);
      },
      /*
       * The same thing on move, which is what makes closing on scroll
       * recoverable.
       *
       * A scroll clears the key while the pointer is still inside the cell, and
       * React fires no second enter for an element the pointer never left - so
       * without this the preview stayed shut until the pointer left the cell and
       * came back. Now any movement inside it re-opens. Resting still after a
       * scroll leaves it shut, which is the point of closing on scroll.
       *
       * Free when nothing changed: setting state to the value it already holds
       * makes React bail out without re-rendering.
       */
      onPointerMove: (event: React.PointerEvent): void => {
        if (event.pointerType !== 'mouse') return;
        setActiveKey(key);
      },
      /*
       * Cleared unconditionally, without checking the pointer type: whatever
       * opened it, a leave or a cancel should shut it. The guard belongs on the
       * way in only.
       */
      onPointerLeave: (): void => setActiveKey(null),
      onPointerCancel: (): void => setActiveKey(null),
    }),
    [],
  );

  useEffect(() => {
    if (activeKey === null) return;

    const clear = (): void => setActiveKey(null);
    /*
     * Capture, because a scroll event does not bubble - a listener on window
     * only hears about scrolling inside the page at all during the capture
     * phase. Passive, because this must never be able to hold up a scroll.
     */
    window.addEventListener('scroll', clear, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', clear, { capture: true });
  }, [activeKey]);

  return { activeKey, handlers };
}
